import { Component, ViewChild, EventEmitter, Output, AfterViewInit, Input, ChangeDetectorRef, forwardRef } from '@angular/core';
import { GoogleMapService } from '../../modules/google-maps/google-map.service';
import { AddressType } from '../address-autocomplete/address-autocomplete.component';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-address-search-input',
  templateUrl: './address-search-input.component.html',
  styleUrls: ['./address-search-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressSearchInputComponent),
      multi: true
    }
  ]
})
export class AddressSearchInputComponent implements AfterViewInit, ControlValueAccessor {
  @Input() placeholder: string = 'Address Search';
  @Input() label: string = 'Address';
  @Input() loadAPI: boolean = false;

  @Output() addressSelected = new EventEmitter<any>();
  @Output() setTimezone = new EventEmitter<any>();

  @ViewChild('addresstext', { static: false }) addresstext: any;

  public searchString: string = '';
  public onChange: any = () => { };
  public onTouch: any = () => { };

  constructor(
    private ref: ChangeDetectorRef,
    private _mapService: GoogleMapService
  ) { }

  ngAfterViewInit() {
    if (this.loadAPI) {
      this._mapService.loadGoogleMapApi().subscribe(() => {
        this.getPlaceAutocomplete();
      });
    } else {
      this.getPlaceAutocomplete();
    }
  }

  private extractAddress(components: any[], type: AddressType) {
    if (!components) return '';
    return components
      .filter((component) => component.types.indexOf(type) !== -1)
      .map((item) => (type === AddressType.STATE) ? item.short_name : item.long_name).pop() || '';
  }

  private getPlaceAutocomplete() {
    this.addresstext.input.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    });

    const autocomplete = new google.maps.places.Autocomplete(this.addresstext.input.nativeElement, {
      componentRestrictions: { country: 'US' }
    });

    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace();

      if (place.geometry && place.geometry.location) {
        this._mapService.getTimezone(place.geometry.location.lat(), place.geometry.location.lng()).subscribe(result => {
          this.setTimezone.emit(result);
        }, error => {
          console.log(error);
        });

        const foundAddress = {
          address1: `${this.extractAddress(place.address_components, AddressType.STREET_NUMBER)} ${this.extractAddress(place.address_components, AddressType.STREET)}`.trim(),
          address2: this.extractAddress(place.address_components, AddressType.STREET2),
          city: this.extractAddress(place.address_components, AddressType.CITY) || this.extractAddress(place.address_components, AddressType.CITY2) || this.extractAddress(place.address_components, AddressType.CITY3),
          state: this.extractAddress(place.address_components, AddressType.STATE),
          postal: this.extractAddress(place.address_components, AddressType.POSTAL_CODE),
          country: this.extractAddress(place.address_components, AddressType.COUNTRY),
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng()
        };
        this.searchString = place.formatted_address || foundAddress.address1;
        this.addressSelected.emit(foundAddress);
        this.onChange({
          ...foundAddress,
          formatted_address: place.formatted_address
        });
        this.ref.detectChanges();
      }
    });
  }

  public onSearchChange(value: string | null): void {
    this.searchString = value || '';
    if (!value) {
      this.addressSelected.emit(null);
      this.onChange(null);
    }
  }

  // ControlValueAccessor methods
  writeValue(obj: any): void {
    if (typeof obj === 'string') {
      this.searchString = obj;
    } else if (obj) {
      // Prefer formatted address if available
      this.searchString =
        obj.formatted_address ||
        [obj.address1, obj.city, obj.state, obj.postal, obj.country]
          .filter(Boolean)
          .join(', ');
    } else {
      this.searchString = '';
    }

    this.ref.markForCheck();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
}
